import { Check } from "lucide-react";
import { ChapterTag, MaskedLines, FadeUp } from "./Reveal";
import { useLead } from "../context/LeadContext";

const PLANS = [
  {
    name: "Essential Protection",
    tag: "Targeted Relief",
    includes: ["Detailed property inspection", "Targeted treatment for one active pest", "Basic prevention guidance"],
    ideal: "Apartments with a specific pest issue",
    featured: false,
  },
  {
    name: "Complete Protection",
    tag: "Year-Round Cover",
    includes: ["Everything in Essential", "Scheduled maintenance visits", "Cockroach, ant & fly cover", "Preventive perimeter care"],
    ideal: "Families wanting ongoing peace of mind",
    featured: true,
  },
  {
    name: "Total Property Protection",
    tag: "Whole-Property Defence",
    includes: ["Everything in Complete", "Rodent & mosquito management", "Priority same-day response"],
    ideal: "Villas and larger homes",
    featured: false,
  },
  {
    name: "Specialized Treatment",
    tag: "Focused Programs",
    includes: ["Termite treatment programs", "Bed bug eradication programs", "Commercial pest management"],
    ideal: "Businesses and stubborn infestations",
    featured: false,
  },
];

export const Packages = () => {
  const { openModal } = useLead();
  return (
    <section id="packages" data-testid="packages-section" className="py-24 lg:py-32 bg-cream border-y border-border scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <ChapterTag index="07" label="Protection Plans" />
        <div className="mt-10 max-w-3xl">
          <h2 data-testid="packages-headline" className="font-display font-semibold text-charcoal tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[<>Protection plans, not</>, <span className="italic text-wine">one-off spray jobs.</span>]} />
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {PLANS.map((p, i) => (
            <FadeUp key={p.name} delay={i * 0.09} className={`flex flex-col border p-8 lg:p-9 ${p.featured ? "bg-wine text-ivory border-wine" : "bg-ivory border-border"}`}>
              <span className={`text-[10px] tracking-[0.3em] uppercase font-bold ${p.featured ? "text-gold" : "text-gold"}`}>{p.tag}</span>
              <h3 data-testid={`package-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-display text-2xl font-semibold mt-3 leading-snug">{p.name}</h3>
              <ul className="mt-6 space-y-3 flex-1">
                {p.includes.map((inc) => (
                  <li key={inc} className="flex gap-2.5 text-sm leading-relaxed">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.featured ? "text-gold" : "text-wine"}`} strokeWidth={2} />
                    <span className={p.featured ? "text-ivory/80" : "text-stone2"}>{inc}</span>
                  </li>
                ))}
              </ul>
              <p className={`text-[11px] tracking-[0.14em] uppercase font-bold mt-7 pt-5 border-t ${p.featured ? "border-ivory/20 text-ivory/60" : "border-border text-stone2"}`}>
                Ideal for: {p.ideal}
              </p>
              <button
                data-testid={`package-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => openModal()}
                className={`mt-6 py-3.5 text-[12px] font-bold tracking-[0.14em] uppercase transition-colors duration-300 ${p.featured ? "bg-ivory text-wine hover:bg-gold hover:text-charcoal" : "bg-wine text-ivory hover:bg-wine-light"}`}
              >
                Request Pricing
              </button>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.3}>
          <p className="text-xs text-stone2 mt-8 italic">Plan inclusions are illustrative placeholders — final plans and pricing are confirmed after your free inspection.</p>
        </FadeUp>
      </div>
    </section>
  );
};
